import _ from "lodash";
import React from "react";
import ACLStore from "lib/aclStore";

import {
  CalendarScheduleWeekOutlined,
  WeightOutlined,
  ClassProgressSummaryOutlined,
  ListNumberedOutlined,
  PencilLineOutlined,
  CalculatorOutlined,
} from "@toddle-edu/ds-icons";
import { Avatar, Button, Tooltip } from "@toddle-edu/ds-web";
import {
  NoDataIllustration,
  AcademicHonestyIllustration,
  NoServiceAsActionInsightsIllustration,
} from "@toddle-edu/ds-theme";
import {
  CURRICULUM_TYPE_UBD,
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_MYP,
  SCORE,
  MANUAL,
  EQUAL_WEIGHTS,
  WEIGHTS_PROPORTIONAL,
  CUSTOM_WEIGHTS,
  WHOLE_NUMBER,
  MYP_CRITERIA,
  PERCENTAGE_UPPERCASE,
  WEIGHT,
  DECIMAL,
  UNWEIGHTED,
  WEIGHTED,
  NONE,
  GRADE_BOUNDARY_SETUP,
  ASSESSMENT_CATEGORY_SETUP,
  LOCAL_GRADE_SETUP,
  FINAL_GRADE,
  FINAL_SCORE,
  LOCAL_GRADE,
  CATEGORY_PLACEHOLDER,
  GRADE,
  SUBJECT,
  DEFAULT,
  ACTIVE,
  CATEGORY_SCORE,
  CATEGORY_GRADE,
  MYP_CRITERIA_TOTAL_TYPE_ALL_CRITERIA,
  UNLOCKED,
  ASSIGNMENT_TYPE_QUICK_TASK,
  TASK_TYPE_QUICK_TASK,
} from "Constants/stringConstants";
import overallSetupClasses from "GradingSetup/routes/OverallSetup/components/OverallSetupConfiguration/OverallSetupConfiguration.scss";
import { colors, getCloudUrl } from "Constants";
import { getSettingValue } from "modules/PermissionModule";
import { getCurriculumBasedRoute } from "ScoreCategories/modules/ScoreCategoriesModule";
import { ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION } from "Constants/permissionConstants";
import { getIsAccountConfigurationEnabled } from "Utils/ACLStoreUtils";

const styles = {
  disabledText: {
    color: colors.textDisabled,
  },
  studentAvatarStyles: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
};

export const getOverallSetupHeaderText = ({
  t,
  curriculumType,
  showOverallIbGradeSetting = true,
}) => {
  if (!showOverallIbGradeSetting) {
    return t("academicSetup:overall_score_setup");
  }

  switch (curriculumType) {
    case CURRICULUM_TYPE_UBD:
      return t("academicSetup:overall_score_and_grade_setup");
    case CURRICULUM_TYPE_PYP:
      return t("academicSetup:overall_score_setup");
    case CURRICULUM_TYPE_DP:
      return t("academicSetup:overall_score_and_ibdp_grade_setup");
    case CURRICULUM_TYPE_MYP:
      return t("academicSetup:overall_score_and_ibmpyp_grade_setup");
  }
};

export const getOverallSetupHeaderSubtext = ({
  t,
  curriculumType,
  showOverallIbGradeSetting,
}) => {
  if (!showOverallIbGradeSetting) {
    return "";
  }
  switch (curriculumType) {
    case CURRICULUM_TYPE_DP:
      return t(
        "academicSetup:configure_how_the_overall_score_and_label1_would_be_calcuated_for_label2",
        {
          label1: t("academicSetup:ibdp_grade"),
          label2: t("common:grading_period_plural"),
        }
      );
    case CURRICULUM_TYPE_UBD:
      return t(
        "academicSetup:configure_how_the_overall_score_and_label1_would_be_calcuated_for_label2_and_label3",
        {
          label1: t("common:grade_lowercase"),
          label2: t("academicSetup:assessment_categories"),
          label3: t("common:grading_period_plural"),
        }
      );

    case CURRICULUM_TYPE_MYP:
      return t(
        "academicSetup:configure_how_the_overall_score_and_label1_would_be_calcuated_for_label2",
        {
          label1: t("academicSetup:ibmyp_grade"),
          label2: t("common:grading_period_plural"),
        }
      );
    default:
      return "";
  }
};

const getShowSection = ({ checkListItems }) =>
  _.some(checkListItems, ({ perm }) =>
    !_.isNil(perm) ? ACLStore.can(perm) : true
  );

export const getChecklistItems = ({
  t,
  curriculumType,
  isOverallScoreEnabled,
  isLocalGradeEnabled,
}) => {
  const { gradeLabel } = getOverallGradeAndGradeLabel({ t, curriculumType });

  let gradeBoundarySetupLabel = t("academicSetup:ibdp_grade_boundaries");
  let gradeBoundaryRoute = "grade-boundary-setup";
  let localGradeRoute = "local-grade-setup";
  let localGradeLabel = t("common:local_grades");
  let gradeBoundaryPerm = "AdminPortal:GradeBoundarySetup";
  let localGradePerm = "AdminPortal:LocalGradeSetup";
  let gradeBoundaryKey = "GRADE_BOUNDARY_SETUP";

  if (curriculumType === CURRICULUM_TYPE_MYP) {
    gradeBoundarySetupLabel = t("academicSetup:ibmyp_grade_boundaries");
  }

  if (curriculumType === CURRICULUM_TYPE_UBD) {
    gradeBoundarySetupLabel = t("academicSetup:grade_boundaries");
    gradeBoundaryRoute = "grade-boundary-setup";
    localGradeRoute = "overall-grade-setup";
    localGradeLabel = t("academicSetup:overall_grade_setup");
    gradeBoundaryPerm = "AdminPortal:LocalGradeSetup";
    localGradePerm = "AdminPortal:UBDLocalGrades";
    gradeBoundaryKey = "UBD_GRADE_BOUNDARY_SETUP";
  }

  const localGradeSubtextLabel = _.includes(
    [CURRICULUM_TYPE_UBD, CURRICULUM_TYPE_PYP],
    curriculumType
  )
    ? t("common:score_lowercase")
    : `${t("common:score_lowercase")}/${gradeLabel}`;

  const aggregateSetupLabel = _.includes(
    [CURRICULUM_TYPE_UBD, CURRICULUM_TYPE_PYP],
    curriculumType
  )
    ? t("academicSetup:score_aggregation")
    : t("academicSetup:score_and_grade_aggregation");

  const gradeBoundaryItem =
    curriculumType !== CURRICULUM_TYPE_PYP
      ? [
          {
            id: gradeBoundaryKey,
            icon: (
              <div className={overallSetupClasses.iconBackgroundPink}>
                <ListNumberedOutlined variant="critical" />
              </div>
            ),
            label: gradeBoundarySetupLabel,
            subText: t(
              "academicSetup:grade_boundary_setup_subtext_with_label",
              {
                label: gradeLabel,
              }
            ),
            route: gradeBoundaryRoute,
            perm: gradeBoundaryPerm,
          },
        ]
      : [];

  const gradingSetupCheckListItems = [
    {
      id: "ASSESSMENT_SETUP",
      icon: (
        <div className={overallSetupClasses.iconBackgroundViolet}>
          <WeightOutlined />
        </div>
      ),
      label: t("progressSummary:assessments"),
      subText: t("academicSetup:assessment_setup_subtext"),
      route: "assessment-setup",
      perm: "AdminPortal:AssessmentCategoriesV2",
    },
    {
      id: "CUMULATIVE_SCORES",
      icon: (
        <div className={overallSetupClasses.iconBackgroundGreen}>
          <CalendarScheduleWeekOutlined variant="success" />
        </div>
      ),
      label: t("academicSetup:cumulative_scores"),
      subText: t("academicSetup:cumulative_score_setup_subtext"),
      route: "cumulative-score-setup",
      perm: "AdminPortal:ShowCumulativeColumns",
    },
    ...gradeBoundaryItem,
    {
      id: "LOCAL_GRADE_SETUP",
      icon: (
        <div className={overallSetupClasses.iconBackgroundBlue}>
          <ClassProgressSummaryOutlined variant="link" />
        </div>
      ),
      label: localGradeLabel,
      subText: t("academicSetup:local_grade_setup_subtext_with_label", {
        label: localGradeSubtextLabel,
      }),
      route: localGradeRoute,
      perm: localGradePerm,
    },
    {
      id: "AGGREGATE_SETUP",
      icon: (
        <div className={overallSetupClasses.iconBackgroundViolet}>
          <CalculatorOutlined variant="link" />
        </div>
      ),
      label: aggregateSetupLabel,
      subText: t("academicSetup:score_and_grade_aggregation_setup_subtext"),
      route: "aggregate-setup",
      perm: "FeatureFlag:ScoreAndGradeAggregation",
    },
  ];

  const setupCheckListItems = [
    {
      id: "NOMENCLATURE_SETUP",
      icon: (
        <div className={overallSetupClasses.iconBackgroundViolet}>
          <PencilLineOutlined />
        </div>
      ),
      label: t("common:nomenclature"),
      subText: t("academicSetup:nomenclature_option_subtext", {
        labels: t("common:join_array_with_and", {
          array: getNomenclatureListForSubtext({
            curriculumProgramType: curriculumType,
            isOverallScoreEnabled,
            isLocalGradeEnabled,
            t,
          }),
        }),
      }),
      route: "nomenclature-setup",
      perm: "FeatureFlag:EnableNomenclatureForCriteriaSet",
    },
  ];

  return [
    {
      id: "GRADING_SETUP_WORKFLOW",
      label: t("academicSetup:grading_setup_workflow"),
      showSection: getShowSection({
        checkListItems: gradingSetupCheckListItems,
      }),
      checkListItems: gradingSetupCheckListItems,
    },
    {
      id: "SETUP",
      label: t("common:setup"),
      showSection: getShowSection({ checkListItems: setupCheckListItems }),
      checkListItems: setupCheckListItems,
    },
  ];
};

const getExampleGradeBoundariesImagesUrl = ({
  isCurriculumMYP,
  isCurriculumUBD,
}) => {
  let sampleGradesTableImageUrl = "";
  let gradebookExampleTableImageUrl = "";
  const cloudUrl = getCloudUrl();

  switch (true) {
    case isCurriculumUBD:
      sampleGradesTableImageUrl = `${cloudUrl}/assets/webapp/assessment_settings/OVERALL_GRADE_SCALE_UBD.png`;
      gradebookExampleTableImageUrl = `${cloudUrl}/assets/webapp/assessment_settings/OVERALL_GRADE_GRADEBOOK_EXAMPLE.png`;
      break;
    case isCurriculumMYP:
      sampleGradesTableImageUrl = `${cloudUrl}/assets/webapp/assessment_settings/SAMPLE_MYP_OVERALL_GRADE_TABLE.png`;
      gradebookExampleTableImageUrl = `${cloudUrl}/assets/webapp/assessment_settings/MYP_OVERALL_GRADE_EXAMPLE.png`;
      break;
    default:
      sampleGradesTableImageUrl = `${cloudUrl}/assets/webapp/assessment_settings/SAMPLE_GRADE_BOUNDARY_TABLE.png`;
      gradebookExampleTableImageUrl = `${cloudUrl}/assets/webapp/assessment_settings/OVERALL_IB_GRADE_EXAMPLE_TABLE.png`;
  }

  return {
    sampleGradesTableImageUrl,
    gradebookExampleTableImageUrl,
  };
};

export const getOverallGradeCalculationMethodHeaderSubtext = ({
  isGradeCalculatedFromScore,
  isCurriculumMYP,
  isCurriculumUBD,
  gradesLabel,
  t,
}) => {
  if (isGradeCalculatedFromScore) {
    return t(
      "academicSetup:overall_grade_will_be_automatically_calculated_explanation_subtext_with_label",
      { label: gradesLabel }
    );
  } else {
    if (isCurriculumMYP) {
      return t(
        "academicSetup:overall_grade_calculated_from_myp_criteria_explanation"
      );
    } else {
      const gradeLabel = isCurriculumUBD
        ? t("common:grade_lowercase")
        : t("common:ib_grade");

      return t(
        "academicSetup:manual_grade_assignment_explanation_subtext_with_label",
        { label: gradeLabel }
      );
    }
  }
};

const getManualGradeSelectionExampleImagesUrl = ({
  isCurriculumMYP,
  isCurriculumUBD,
}) => {
  let manualGradeSelectionExampleTableUrl = "";
  let manualGradeSelectionExampleGradingHistoryUrl = "";
  const cloudUrl = getCloudUrl();

  switch (true) {
    case isCurriculumMYP:
      manualGradeSelectionExampleTableUrl = `${cloudUrl}/assets/webapp/assessment_settings/OVERALL_IB_GRADE_MYP_CRITERIA.png`;
      break;
    case isCurriculumUBD:
      manualGradeSelectionExampleTableUrl = `${cloudUrl}/assets/webapp/assessment_settings/OVERALL_GRADE_MANUALLY_ASSIGNED.png`;
      manualGradeSelectionExampleGradingHistoryUrl = `${cloudUrl}/assets/webapp/assessment_settings/ASSIGN_OVERALL_GRADE_POPUP.png`;
      break;
    default:
      manualGradeSelectionExampleTableUrl = `${cloudUrl}/assets/webapp/assessment_settings/OVERALL_IB_GRADE_MANUALLY_ASSIGNED.png`;
      manualGradeSelectionExampleGradingHistoryUrl = `${cloudUrl}/assets/webapp/ib_grade_grading_history_chart.png`;
  }

  return {
    manualGradeSelectionExampleTableUrl,
    manualGradeSelectionExampleGradingHistoryUrl,
  };
};

export const getOverallGradeCalculationMethodInfoItems = ({
  t,
  isCurriculumMYP,
  isCurriculumUBD,
  isGradeCalculatedFromScore,
}) => {
  const gradesLabel = isCurriculumUBD
    ? t("common:grades_lowercase")
    : t("common:ib_grades");

  const headerSubtext = getOverallGradeCalculationMethodHeaderSubtext({
    isGradeCalculatedFromScore,
    isCurriculumMYP,
    isCurriculumUBD,
    gradesLabel,
    t,
  });

  const sampleGradesTableLabel = t("academicSetup:sample_with_label", {
    label: t("academicSetup:grade_boundaries"),
  });

  const {
    sampleGradesTableImageUrl,
    gradebookExampleTableImageUrl,
  } = getExampleGradeBoundariesImagesUrl({ isCurriculumUBD, isCurriculumMYP });

  const gradebookExampleSubtext = !isCurriculumMYP
    ? t(
        "academicSetup:each_student_will_be_awarded_an_overall_score_and_equivalent_grade_with_label",
        {
          label: t("common:ib_grade"),
        }
      )
    : "";

  const {
    manualGradeSelectionExampleTableUrl,
    manualGradeSelectionExampleGradingHistoryUrl,
  } = getManualGradeSelectionExampleImagesUrl({
    isCurriculumMYP,
    isCurriculumUBD,
  });

  return {
    headerSubtext,
    sampleGradesTableLabel,
    sampleGradesTableImageUrl,
    gradebookExampleSubtext,
    gradebookExampleTableImageUrl,
    manualGradeSelectionExampleGradingHistoryUrl,
    manualGradeSelectionExampleTableUrl,
  };
};

export const getOverallGradeCalculationMethodSelectionCardOptions = ({
  isCurriculumMYP,
  t,
}) => {
  const options = [
    {
      title: t("academicSetup:auto_calculated_from_score"),
      subtitle: t(
        "academicSetup:grade_automatically_calculated_from_score_subtext"
      ),
      value: SCORE,
    },
  ];

  if (isCurriculumMYP) {
    options.push({
      title: t("academicSetup:auto_calculated_from_myp_criteria"),
      subtitle: t("academicSetup:auto_calculated_from_myp_criteria_subtext"),
      value: MYP_CRITERIA,
    });
  } else {
    options.push({
      title: t("academicSetup:manually_assigned"),
      subtitle: t("academicSetup:grade_manually_assigned_subtext"),
      value: MANUAL,
    });
  }

  return options;
};

export const getFinalGradeSettingButtonLabel = ({
  finalGradeCalculationSettingValue,
  t,
}) => {
  switch (finalGradeCalculationSettingValue) {
    case SCORE:
      return t("academicSetup:auto_calculated_from_score");
    case MANUAL:
      return t("academicSetup:manually_assigned");
    case MYP_CRITERIA:
      return t("academicSetup:auto_calculated_from_myp_criteria");
    case NONE:
      return t("academicSetup:overall_grade_calculation_disabled");
  }
};

export const getMypCriteriaGradeSettingButtonLabel = ({
  mypCriteriaTotalCalculationMethodValue,
  t,
}) => {
  switch (mypCriteriaTotalCalculationMethodValue) {
    case MYP_CRITERIA_TOTAL_TYPE_ALL_CRITERIA:
      return t("academicSetup:only_when_all_criteria_are_rated");
    default: {
      return t("academicSetup:when_one_or_more_criteria_are_graded");
    }
  }
};

export const getRoundOffScoreDropdownOptions = ({
  t,
  shouldUseRoundedCategoryScoresInFinalScoreCalculation,
}) => {
  return shouldUseRoundedCategoryScoresInFinalScoreCalculation
    ? [
        {
          isItemGroup: true,
          key: "1",
          label: t("academicSetup:calculate_overall_score"),
          options: [
            {
              key: DECIMAL,
              label: t("common:in_decimals"),
            },
            {
              key: WHOLE_NUMBER,
              label: t("common:in_whole_numbers"),
            },
          ],
        },
      ]
    : [
        {
          key: DECIMAL,
          label: t("academicSetup:show_as_decimal"),
        },
        {
          key: WHOLE_NUMBER,
          label: t("academicSetup:show_as_whole_numbers"),
        },
      ];
};

export const getCategoryDisplayTypeDropdownOptions = ({
  t,
  isCategoryWeighted,
}) => {
  return [
    {
      key: PERCENTAGE_UPPERCASE,
      label: t("common:total_percentage"),
      value: PERCENTAGE_UPPERCASE,
    },
    {
      key: WEIGHT,
      label: t("common:weight"),
      disabled: !isCategoryWeighted,
      isDisabled: !isCategoryWeighted,
      disabledTooltip: t(
        "academicSetup:category_weighted_display_type_disabled_tooltip"
      ),
      value: WEIGHT,
    },
  ];
};

export const getCategoryDisplayTypeInfoItems = ({ t }) => {
  const cloudUrl = getCloudUrl();

  return {
    weightedCategoryTableUrl: `${cloudUrl}/assets/webapp/assessment_settings/weighted_categories_example_table.png`,
    displayTypeInfoItems: [
      {
        id: WEIGHT,
        label: t("common:weights"),
        url: `${cloudUrl}/assets/webapp/assessment_settings/CATEGORY_WEIGHTS_EXAMPLE_TABLE.png`,
      },
      {
        id: PERCENTAGE_UPPERCASE,
        label: t("common:total_percentage"),
        url: `${cloudUrl}/assets/webapp/assessment_settings/CATEGORY_PERCENTAGE_EXAMPLE_TABLE.png`,
      },
    ],
  };
};

export const getCategoryWeightageTabOptions = ({ t }) => {
  return [
    {
      key: "WEIGHTED_CATEGORIES",
      value: true,
      label: t("academicSetup:weighted_categories"),
      subtext: t("academicSetup:weighted_categories_subtext"),
    },
    {
      key: "UNWEIGHTED_CATEGORIES",
      value: false,
      label: t("academicSetup:unweighted_categories"),
      subtext: t("academicSetup:unweighted_categories_subtext"),
    },
  ];
};

export const getCategoryWeightageInfoItems = ({
  t,
  gradeCalculationMethod,
  selectedTab,
}) => {
  const cloudUrl = getCloudUrl();
  switch (selectedTab) {
    case WEIGHTED: {
      switch (gradeCalculationMethod) {
        case EQUAL_WEIGHTS: {
          return {
            exampleTable: `${cloudUrl}/assets/webapp/percentage_equal_weights_example_table.png`,
            exampleCalculation: `${cloudUrl}/assets/webapp/percentage_equal_weights_example_calculation.png`,
            exampleResult: `${cloudUrl}/assets/webapp/percentage_equal_weights_example_res.png`,
            gradeCalculationMethodLabel: t("academicSetup:equal_weights_label"),
            exampleTableV2: `${cloudUrl}/assets/webapp/category_setup/equal_weights_example_table.png`,
            exampleCalculationV2: `${cloudUrl}/assets/webapp/category_setup/equal_weights_example_calculation.png`,
            exampleResultInDecimals: `${cloudUrl}/assets/webapp/category_setup/equal_weights_example_result_in_decimals.png`,
            exampleResultInWholeNumbers: `${cloudUrl}/assets/webapp/category_setup/equal_weights_example_result_in_whole_numbers.png`,
          };
        }
        case WEIGHTS_PROPORTIONAL: {
          return {
            exampleTable: `${cloudUrl}/assets/webapp/percentage_weights_proportional_example_table.png`,
            exampleCalculation: `${cloudUrl}/assets/webapp/percentage_weights_proportional_example_calculation.png`,
            exampleResult: `${cloudUrl}/assets/webapp/percentage_weights_proportional_example_res.png`,
            gradeCalculationMethodLabel: t(
              "academicSetup:weights_proportional_to_max_score"
            ),
            exampleTableV2: `${cloudUrl}/assets/webapp/category_setup/weights_proportional_example_table.png`,
            exampleCalculationV2: `${cloudUrl}/assets/webapp/category_setup/weights_proportional_example_calculation.png`,
            exampleResultInDecimals: `${cloudUrl}/assets/webapp/category_setup/weights_proportional_example_result_in_decimals.png`,
            exampleResultInWholeNumbers: `${cloudUrl}/assets/webapp/category_setup/weights_proportional_example_result_in_whole_numbers.png`,
          };
        }
        case CUSTOM_WEIGHTS: {
          return {
            exampleTable: `${cloudUrl}/assets/webapp/percentage_custom_weights_example_table.png`,
            exampleCalculation: `${cloudUrl}/assets/webapp/percentage_custom_weights_example_calculation.png`,
            exampleResult: `${cloudUrl}/assets/webapp/percentage_custom_weights_example_res.png`,
            gradeCalculationMethodLabel: t(
              "academicSetup:custom_weights_label"
            ),
            exampleTableV2: `${cloudUrl}/assets/webapp/category_setup/custom_weights_example_table.png`,
            exampleCalculationV2: `${cloudUrl}/assets/webapp/category_setup/custom_weights_example_calculation.png`,
            exampleResultInDecimals: `${cloudUrl}/assets/webapp/category_setup/custom_weights_example_result_in_decimals.png`,
            exampleResultInWholeNumbers: `${cloudUrl}/assets/webapp/category_setup/custom_weights_example_result_in_whole_numbers.png`,
          };
        }
        default: {
          return {};
        }
      }
    }
    case UNWEIGHTED: {
      switch (gradeCalculationMethod) {
        case EQUAL_WEIGHTS: {
          return {
            exampleTable: `${cloudUrl}/assets/webapp/assessment_settings/UNWEIGHTED_CATEGORIES_EQUAL_WEIGHTS_EX_TABLE.png`,
            exampleCalculation: `${cloudUrl}/assets/webapp/assessment_settings/UNWEIGHTED_CATEGORIES_EQUAL_WEIGHTS_CALCULATION.png`,
            exampleResult: "",
            gradeCalculationMethodLabel: t("academicSetup:equal_weights_label"),
            exampleTableV2: `${cloudUrl}/assets/webapp/category_setup/unweighted_equal_weights_example_table.png`,
            exampleResultInDecimals: `${cloudUrl}/assets/webapp/category_setup/unweighted_equal_weights_example_result_in_decimals.png`,
            exampleResultInWholeNumbers: `${cloudUrl}/assets/webapp/category_setup/unweighted_equal_weights_example_result_in_whole_numbers.png`,
          };
        }
        case WEIGHTS_PROPORTIONAL: {
          return {
            exampleTable: `${cloudUrl}/assets/webapp/assessment_settings/UNWEIGHTED_CATEGORIES_EQUAL_WEIGHTS_EX_TABLE.png`,
            exampleCalculation: `${cloudUrl}/assets/webapp/assessment_settings/UNWEIGHTED_CATEGORIES_WEIGHTS_PROPORTIONAL_CALCULATION.png`,
            exampleResult: "",
            gradeCalculationMethodLabel: t(
              "academicSetup:weights_proportional_to_max_score"
            ),
            exampleTableV2: `${cloudUrl}/assets/webapp/category_setup/unweighted_weights_proportional_example_table.png`,
            exampleResultInDecimals: `${cloudUrl}/assets/webapp/category_setup/unweighted_weights_proportional_example_result_in_decimals.png`,
            exampleResultInWholeNumbers: `${cloudUrl}/assets/webapp/category_setup/unweighted_weights_proportional_example_result_in_whole_numbers.png`,
          };
        }
        case CUSTOM_WEIGHTS: {
          return {
            exampleTable: `${cloudUrl}/assets/webapp/assessment_settings/UNWEIGHTED_CATEGORIES_CUSTOM_WEIGHTS_EX_TABLE.png`,
            exampleCalculation: `${cloudUrl}/assets/webapp/assessment_settings/UNWEIGHTED_CATEGORIES_CUSTOM_WEIGHTS_CALCULATION.png`,
            exampleResult: `${cloudUrl}/assets/webapp/assessment_settings/UNWEIGHTED_CATEGORIES_CUSTOM_WEIGHTS_RESULT.png`,
            gradeCalculationMethodLabel: t(
              "academicSetup:custom_weights_label"
            ),
            exampleTableV2: `${cloudUrl}/assets/webapp/category_setup/unweighted_custom_weights_example_table.png`,
            exampleResultInDecimals: `${cloudUrl}/assets/webapp/category_setup/unweighted_custom_weights_example_result_in_decimals.png`,
            exampleResultInWholeNumbers: `${cloudUrl}/assets/webapp/category_setup/unweighted_custom_weights_example_result_in_whole_numbers.png`,
          };
        }
        default: {
          return {};
        }
      }
    }
  }
};

export const getIsGradingSetupChecklistItemUnavailable = ({
  t,
  isCurriculumMYP,
  isScoreCalculatedForGradingPeriods,
  finalGradeCalculationSettingValue,
  id,
}) => {
  switch (id) {
    case "GRADE_BOUNDARY_SETUP": {
      let unavailableText = t(
        "academicSetup:not_available_since_ib_grades_assigned_manually"
      );

      if (isCurriculumMYP) {
        unavailableText = t(
          "academicSetup:not_available_since_ib_grade_calculated_from_myp_criteria"
        );
      }

      return {
        isUnavailable: isCurriculumMYP
          ? finalGradeCalculationSettingValue === MYP_CRITERIA
          : finalGradeCalculationSettingValue === MANUAL,
        unavailableText,
      };
    }
    case "CUMULATIVE_SCORES":
      return {
        isUnavailable: !isScoreCalculatedForGradingPeriods,
        unavailableText: t(
          "academicSetup:not_available_since_overall_score_calculation_not_enabled"
        ),
      };
    case "UBD_GRADE_BOUNDARY_SETUP":
      return {
        isUnavailable: finalGradeCalculationSettingValue === NONE,
        unavailableText: t(
          "academicSetup:not_available_since_overall_grade_is_not_enabled"
        ),
      };
    default: {
      return {
        isUnavailable: false,
        unavailableText: "",
      };
    }
  }
};

export const getIsNewAssessmentSettingsEnabled = () => {
  return ACLStore.can("FeatureFlag:AssessmentSettings");
};

export const getCanOverrideOverallScore = () => {
  return ACLStore.can("FeatureFlag:OverrideScoresInGradebook");
};

export const getCanOverrideFinalGrade = ({ curriculumProgramId }) => {
  if (getIsNewAssessmentSettingsEnabled()) {
    const canOverrideFinalGrade = getSettingValue({
      name: OVERRIDE_FINAL_GRADE_SETTING,
      currentCurriculumProgramId: curriculumProgramId,
    });

    return canOverrideFinalGrade;
  } else {
    return ACLStore.can("StaffPermission:OverrideFinalGrade");
  }
};

export const USE_WEIGHTAGE_FOR_CATEGORY_SETTING = "UseWeightageForCategory";
export const OVERALL_SCORE_FOR_GRADING_SETUP_SETTING =
  "OverallScoreForGradingPeriod";
export const CATEGORY_FINAL_SCORE_DISPLAY_TYPE_SETTING =
  "CategoryFinalScoreDisplayType";
export const GRADE_CALCULATION_METHOD_SETTING = "GradeCalculationMethod";
export const OVERRIDE_FINAL_GRADE_SETTING = "OverrideFinalGradeSetting";
export const ROUND_OVERALL_SCORE_SETTING = "RoundOverallScore";
export const FINAL_GRADE_CALCULATION_SETTING = "FinalGradeCalculationSetting";
export const IL_OVERALL_GRADE_CALCULATION_SETTING = "ShowOverallGradeForIl";
export const IL_LOCAL_GRADE_CALCULATION_SETTING = "ShowLocalGradeForIl";
export const LOCAL_GRADE_CALCULATION_BASED_ON_SETTING =
  "LocalGradeCalculationBasedOn";
export const MYP_CRITERIA_TOTAL_CALCULATION_BASED_ON_SETTING =
  "CalculateMYPCriteriaTotalMethod";
export const TERM_FINAL_SCORE_DISPLAY_TYPE = "TermFinalScoreDisplayType";
export const DECIMAL_POINTS_FOR_FINAL_SCORE_SETTING =
  "DecimalPointsForFinalScore";
export const DECIMAL_TYPE_FOR_FINAL_SCORE_SETTING = "DecimalTypeForFinalScore";
export const SCORE_AGGREGATION_SETTING = "EnableScoreAggregation";

export const getGradeCalculationMethodInfoItems = ({
  t,
  isGradingSetupV2 = false,
}) => {
  const cloudUrl = getCloudUrl();

  const equalWeightCalculationImage = `${cloudUrl}/assets/webapp/equal_weights_calculation.png`;
  const weightsProportionalCalculationImage = `${cloudUrl}/assets/webapp/weights_proportional_calculation.png`;
  const customWeightCalculationImage = `${cloudUrl}/assets/webapp/custom_weights_calculation.png`;

  return [
    {
      key: EQUAL_WEIGHTS,
      label: isGradingSetupV2
        ? t("common:average")
        : t("academicSetup:equal_weights_label"),
      subText: t("academicSetup:all_assessments_will_have_equal_weightages"),
      exampleTable: `${cloudUrl}/assets/webapp/assessment_settings/EQUAL_WEIGHTS_TASKS_EXAMPLE_TABLE.png`,
      exampleCalculation: equalWeightCalculationImage,
    },
    {
      key: WEIGHTS_PROPORTIONAL,
      label: t("academicSetup:weights_proportional_label"),
      subText: t("academicSetup:proportional_weights_info_text"),
      exampleTable: `${cloudUrl}/assets/webapp/assessment_settings/EQUAL_WEIGHTS_TASKS_EXAMPLE_TABLE.png`,
      exampleCalculation: weightsProportionalCalculationImage,
    },
    {
      key: CUSTOM_WEIGHTS,
      label: isGradingSetupV2
        ? t("academicSetup:weighted_average")
        : t("academicSetup:custom_weights_label"),
      subText: t("academicSetup:custom_weights_info_text"),
      exampleTable: `${cloudUrl}/assets/webapp/assessment_settings/CUSTOM_WEIGHTS_TASKS_EXAMPLE_TABLE.png`,
      exampleCalculation: customWeightCalculationImage,
    },
  ];
};

const getAssessmentSettingsConfigurationDefaultValue = ({ configuration }) => {
  switch (configuration) {
    case USE_WEIGHTAGE_FOR_CATEGORY_SETTING:
    case OVERALL_SCORE_FOR_GRADING_SETUP_SETTING:
      return true;
    case FINAL_GRADE_CALCULATION_SETTING:
      return SCORE;
  }
};

const getIsAssessmentSettingsConfigurationEnabledByName = ({
  curriculumProgramId,
  settingName,
  gradeId,
}) => {
  const isNewAssessmentSettingsEnabled = getIsNewAssessmentSettingsEnabled();

  if (!isNewAssessmentSettingsEnabled) {
    const defaultValue = getAssessmentSettingsConfigurationDefaultValue({
      configuration: settingName,
    });
    return defaultValue;
  }

  const value = getSettingValue({
    name: settingName,
    currentCurriculumProgramId: curriculumProgramId,
    gradeId,
  });

  return value;
};

export const getCanDefineGradeBoundary = ({
  curriculumType,
  isScoreV2,
  curriculumProgramId,
  subjectType,
  gradeId,
  finalGradeCalculationMethod: finalGradeCalculationMethodParam,
}) => {
  let curriculumWiseCondition;

  let finalGradeCalculationMethod = finalGradeCalculationMethodParam;

  if (!finalGradeCalculationMethod) {
    finalGradeCalculationMethod = getIsAssessmentSettingsConfigurationEnabledByName(
      {
        curriculumProgramId,
        settingName: FINAL_GRADE_CALCULATION_SETTING,
        gradeId,
      }
    );
  }

  switch (curriculumType) {
    case CURRICULUM_TYPE_DP:
      {
        const isGradeManuallyAssigned = finalGradeCalculationMethod === MANUAL;
        curriculumWiseCondition =
          !_.includes(["CAS", "EE"], subjectType) && !isGradeManuallyAssigned;
      }
      break;
    case CURRICULUM_TYPE_MYP:
      {
        curriculumWiseCondition = finalGradeCalculationMethod !== MYP_CRITERIA;
      }
      break;
    case CURRICULUM_TYPE_UBD:
      {
        curriculumWiseCondition = finalGradeCalculationMethod !== NONE;
      }
      break;
    case CURRICULUM_TYPE_PYP: {
      curriculumWiseCondition = false;
    }
  }

  return curriculumWiseCondition && isScoreV2;
};

export const getAdditionalConfigurationsToUpdate = ({
  overallScoreSettingValue,
  isCurriculumPYP,
  isCurriculumMYP,
  isCurriculumDP,
  isCurriculumUBD,
  showOverallIbGradeSetting,
  finalGradeCalculationSettingValue,
  finalGradeCalculationSetting,
  weightedCategoriesSetting,
  overrideFinalGradeSetting,
  categoryDisplayTypeSetting,
  localGradeCalculationBasedOn,
  scoreAggregationSetting,
}) => {
  const additionalConfigurationsToUpdate = [];

  if (!overallScoreSettingValue) {
    additionalConfigurationsToUpdate.push(
      {
        configuration: weightedCategoriesSetting,
        newValue: false,
      },
      {
        configuration: categoryDisplayTypeSetting,
        newValue: PERCENTAGE_UPPERCASE,
      }
    );

    const localGradeCalculationBasedOnSettingValue = _.get(
      localGradeCalculationBasedOn,
      "value"
    );

    if (
      !_.isEmpty(localGradeCalculationBasedOn) &&
      _.includes(
        ["WEIGHTED_SCORE", "SCORE_BASED_ASSESSMENT"],
        localGradeCalculationBasedOnSettingValue
      ) &&
      (isCurriculumMYP || isCurriculumDP)
    ) {
      additionalConfigurationsToUpdate.push({
        configuration: localGradeCalculationBasedOn,
        newValue: isCurriculumMYP ? "MYP_FINAL_GRADE" : "FINAL_GRADE",
      });
    }

    if (
      !isCurriculumPYP &&
      showOverallIbGradeSetting &&
      finalGradeCalculationSettingValue === SCORE
    ) {
      additionalConfigurationsToUpdate.push({
        configuration: finalGradeCalculationSetting,
        newValue: isCurriculumMYP ? MYP_CRITERIA : MANUAL,
      });
    }

    const canOverrideOverallScore = getCanOverrideOverallScore();

    if (!canOverrideOverallScore && !isCurriculumMYP) {
      additionalConfigurationsToUpdate.push({
        configuration: overrideFinalGradeSetting,
        newValue: true,
      });
    }

    if (
      isScoreAndGradeAggregationEnabled() &&
      (isCurriculumPYP ||
        isCurriculumUBD ||
        ((isCurriculumDP || isCurriculumMYP) &&
          finalGradeCalculationSettingValue == "NONE"))
    ) {
      additionalConfigurationsToUpdate.push({
        configuration: scoreAggregationSetting,
        newValue: false,
      });
    }
  }

  return additionalConfigurationsToUpdate;
};

export const isScoreAndGradeAggregationEnabled = () => {
  return ACLStore.can("FeatureFlag:ScoreAndGradeAggregation");
};

export const getGradeCalculationMethodButtonLabel = ({
  isGradingSetupV2 = false,
  gradeCalculationMethod,
  t,
}) => {
  switch (gradeCalculationMethod) {
    case EQUAL_WEIGHTS:
      return isGradingSetupV2
        ? t("common:average")
        : t("academicSetup:equal_weights_label");
    case WEIGHTS_PROPORTIONAL:
      return t("academicSetup:weights_proportional_label");
    case CUSTOM_WEIGHTS:
      return isGradingSetupV2
        ? t("academicSetup:weighted_average")
        : t("academicSetup:custom_weights_label");
    default:
      return isGradingSetupV2
        ? t("common:select")
        : t("academicSetup:select_a_method");
  }
};

export const getShowCategoryGradeBoundaryToggle = ({
  curriculumType,
  curriculumProgramId,
  subjectType,
  finalGradeCalculationMethod,
  canOverrideFinalGrade,
}) => {
  switch (curriculumType) {
    case CURRICULUM_TYPE_DP:
      return !_.includes(["CAS", "EE"], subjectType);
    case CURRICULUM_TYPE_MYP: {
      let condition = false;

      const isRnPEnabled = ACLStore.can(
        ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION
      );

      if (finalGradeCalculationMethod === MYP_CRITERIA) {
        condition = isRnPEnabled || canOverrideFinalGrade;
      } else {
        condition = true;
      }

      return condition;
    }
    case CURRICULUM_TYPE_UBD: {
      const finalGradeCalculationMethod = getSettingValue({
        currentCurriculumProgramId: curriculumProgramId,
        name: FINAL_GRADE_CALCULATION_SETTING,
      });
      return finalGradeCalculationMethod !== NONE;
    }
    case CURRICULUM_TYPE_PYP:
      return false;
  }
};

export const getLocalGradeCalculatedByLabel = ({ t, selectedOption }) => {
  switch (selectedOption) {
    case "FINAL_GRADE":
      return t("common:ib_grades");
    case "WEIGHTED_SCORE":
      return t("common:weighted_score");
    case "SCORE_BASED_ASSESSMENT":
      return t("common:point_based_assessment");
    case "MYP_FINAL_GRADE":
      return t("common:myp_final_grade");
  }
};

export const getLocalGradesCalculatedByDropdownOptions = ({
  selectedOptions,
  t,
  isOverallScoreEnabled,
}) => {
  return _.map(selectedOptions, option => {
    const value = _.get(option, "value");
    const disabled =
      !isOverallScoreEnabled &&
      _.includes(["SCORE_BASED_ASSESSMENT", "WEIGHTED_SCORE"], value);

    return {
      ...option,
      key: value,
      label: t(`common:${option.label}`),
      disabled,
      disabledTooltip: (
        <span>
          {t(
            "academicSetup:not_available_since_overall_score_calculation_not_enabled"
          )}
        </span>
      ),
    };
  });
};

export const getIsCumulativePeriodsEnabled = () =>
  ACLStore.can("AdminPortal:ShowCumulativeColumns");

export const getIsGradingPeriodLockingEnabled = () => {
  return ACLStore.can("FeatureFlag:EnableGradingPeriodLocking");
};

export const getIsGradingPeriodUnlocked = (
  gradingPeriod,
  assessmentType,
  taskTypeGroup
) => {
  if (
    !getIsGradingPeriodLockingEnabled() ||
    _.isEmpty(gradingPeriod) ||
    (!_.isEmpty(assessmentType) &&
      (assessmentType === ASSIGNMENT_TYPE_QUICK_TASK ||
        taskTypeGroup === TASK_TYPE_QUICK_TASK)) // skip locking status check for quick tasks
  )
    return true;

  return _.get(gradingPeriod, "lockingStatus", UNLOCKED) === UNLOCKED;
};

export const getTermLabels = ({ currentTerm, terms }) => {
  const extractLabels = associatedTerms => {
    return _.reduce(
      associatedTerms,
      (acc, associatedTerm) => {
        const term = _.find(terms, {
          id: _.get(associatedTerm, "academicTerm.id"),
        });

        if (_.get(term, "lockingStatus") === UNLOCKED) {
          const termLabel = _.get(term, "label");

          if (termLabel) {
            acc.push(termLabel);
          }

          if (_.get(term, "type") === "CUMULATIVE") {
            const nestedAssociatedTerms = _.get(
              term,
              "associatedAcademicTermEntities",
              []
            );

            if (!_.isEmpty(nestedAssociatedTerms)) {
              acc.push(...extractLabels(nestedAssociatedTerms));
            }
          }
        }

        return acc;
      },
      []
    );
  };

  const termLabels = extractLabels(
    _.get(currentTerm, "associatedAcademicTermEntities", [])
  );

  return _.join(_.uniq(termLabels), ", ");
};

export const getOrderedOrganizationPointSet = _.memoize(
  ({ node, grades }) => {
    const associatedEntities = _.get(node, "associatedEntities", []);
    const pointSetAssociatedGradeId = _.find(associatedEntities, {
      __typename: "Grade",
    })?.id;
    const associatedGradeData = _.find(grades, {
      id: pointSetAssociatedGradeId,
    });
    return _.get(associatedGradeData, "displaySequence", 0);
  },
  params => JSON.stringify(params)
);

export const getLocalGradeCalculationMethodSelectionCardItems = ({
  t,
  isCurriculumMYP,
  isOverallScoreEnabled,
}) => {
  const scoreMethod = isCurriculumMYP
    ? "SCORE_BASED_ASSESSMENT"
    : "WEIGHTED_SCORE";
  const gradeMethod = isCurriculumMYP ? "MYP_FINAL_GRADE" : "FINAL_GRADE";

  return [
    {
      title: t("academicSetup:auto_calculated_from_overall_score"),
      subtitle: t("academicSetup:local_grade_from_score_subtitle"),
      value: scoreMethod,
      disabled: !isOverallScoreEnabled,
    },
    {
      title: t("academicSetup:auto_calculated_from_overall_grade"),
      subtitle: t("academicSetup:local_grade_from_grade_subtitle"),
      value: gradeMethod,
    },
  ];
};

export const getLocalGradeCalculationMethodInfoItems = ({
  t,
  selectedMethod,
}) => {
  const sampleTableLabel = t("academicSetup:sample_local_grade_scale");

  switch (selectedMethod) {
    case "WEIGHTED_SCORE":
    case "SCORE_BASED_ASSESSMENT":
      return {
        headerSubtext: t(
          "academicSetup:local_grade_calculted_from_score_subtext"
        ),
        sampleTableLabel,
        gradebookExampleSubtext: t(
          "academicSetup:local_grade_calcluated_from_score_gradebook_explanation_subtext"
        ),
      };
    case "FINAL_GRADE":
    case "MYP_FINAL_GRADE":
      return {
        headerSubtext: t(
          "academicSetup:local_grade_calculated_from_grade_subtext"
        ),
        sampleTableLabel,
        gradebookExampleSubtext: t(
          "academicSetup:local_grade_calcluated_from_grade_gradebook_explanation_subtext"
        ),
      };
  }
};

export const getLocalGradeSelectionButtonLabel = ({
  t,
  localGradeCalculatedBySettingValue,
}) => {
  switch (localGradeCalculatedBySettingValue) {
    case "WEIGHTED_SCORE":
    case "SCORE_BASED_ASSESSMENT":
      return t("academicSetup:auto_calculated_from_overall_score");
    case "FINAL_GRADE":
    case "MYP_FINAL_GRADE":
      return t("academicSetup:auto_calculated_from_overall_grade");
    case NONE:
      return (
        <span style={styles.disabledText}>
          {t("academicSetup:local_grade_calculation_disabled")}
        </span>
      );
    default:
      return t("academicSetup:select_a_method");
  }
};

export const getGradeScaleForSampleTable = ({
  isScoreMethod,
  isGradebookExampleTable,
  t,
}) => {
  const values = isScoreMethod
    ? [90, 80, 70, 60, 50, 40, 0]
    : [7, 6, 5, 4, 3, 2, 1];
  const grades = ["A+", "A", "B+", "B", "C", "D", "F"];

  const gradeScale = _.zipWith(grades, values, (grade, _value) => {
    const value = isScoreMethod
      ? t("common:or_higher_with_label", { label: `${_value}%` })
      : _value;

    return {
      grade,
      value,
    };
  });

  // in case of gradebook example table, add a student with avatar to each row
  // also remove the last 4 rows since we will only show first 3 rows
  if (isGradebookExampleTable) {
    gradeScale.splice(3, 4);
    const avatarColors = ["yellow", "pink", "violet"];
    gradeScale.forEach((item, index) => {
      const avatarColor = _.get(avatarColors, index, "yellow");
      const value = _.get(values, index);
      item.value = isScoreMethod ? `${value}%` : value;
      item.student = (
        <div style={styles.studentAvatarStyles}>
          <Avatar
            color={avatarColor}
            size="x-small"
            id={index}
            shade="light"
            iconType="individual"
          />
          {t("common:student")} {index + 1}
        </div>
      );
    });
  }

  return gradeScale;
};

export const getShowLocalGradeToggle = () =>
  ACLStore.can("FeatureFlag:ShowToggleForLocalGrade");

export const getOverallGradeAndGradeLabel = ({ curriculumType, t }) => {
  switch (curriculumType) {
    case CURRICULUM_TYPE_DP:
      return {
        gradeLabel: t("academicSetup:ibdp_grade"),
        overallGradeLabel: t("academicSetup:ibdp_grade"),
      };
    case CURRICULUM_TYPE_MYP:
      return {
        gradeLabel: t("academicSetup:ibmyp_grade"),
        overallGradeLabel: t("academicSetup:ibmyp_grade"),
      };

    case CURRICULUM_TYPE_UBD:
      return {
        gradeLabel: t("common:overall_grade_lowercase"),
        overallGradeLabel: t("common:grade_lowercase"),
      };
    default: {
      return {
        gradeLabel: "",
        overallGradeLabel: "",
      };
    }
  }
};

export const getGradeCalculationModalHeaderText = ({ curriculumType, t }) => {
  switch (curriculumType) {
    case CURRICULUM_TYPE_DP:
      return t("academicSetup:overall_ibdp_grade_setup");
    case CURRICULUM_TYPE_MYP:
      return t("academicSetup:overall_ibmyp_grade_setup");
    case CURRICULUM_TYPE_UBD:
      return t("academicSetup:overall_grade_setup");
  }
};

export const getCategoryGradeBoundaryToggleTooltip = ({
  isAdmin,
  finalGradeCalculationMethod,
  t,
}) => {
  const noFinalGradeCalculationMethodSelected = _.isEmpty(
    finalGradeCalculationMethod
  );

  if (isAdmin) {
    return noFinalGradeCalculationMethodSelected
      ? t("academicSetup:please_select_overall_grade_method")
      : t(
          "academicSetup:please_select_a_calculation_method_to_define_category_grade_boundaries"
        );
  } else {
    return noFinalGradeCalculationMethodSelected
      ? t(
          "academicSetup:once_your_school_admin_selects_an_overall_grade_calculation_method_you_will_be_able_to_define_grade_boundaries"
        )
      : t(
          "academicSetup:once_your_school_admin_defines_calculation_method_you_will_be_able_to_define_category_grade_boundaries"
        );
  }
};

export const getNoSubjectsOrTermsCreatedEmptyStateProps = ({
  t,
  sourceFrom,
  emptyStateType,
  curriculumType,
  curriculumProgramId,
  goToRelativeRoute,
  isAccountPortal,
  isSchoolPortal,
}) => {
  let illustration,
    title,
    subtitle,
    entityLabel,
    buttonProps,
    buttonText,
    route;

  const isCurriculumUBD = curriculumType === CURRICULUM_TYPE_UBD;
  const isCurriculumMYP = curriculumType === CURRICULUM_TYPE_MYP;
  const isCurriculumDP = curriculumType === CURRICULUM_TYPE_DP;

  switch (sourceFrom) {
    case GRADE_BOUNDARY_SETUP:
      {
        if (isCurriculumUBD) {
          entityLabel = t("academicSetup:grade_boundaries");
        } else {
          entityLabel = t("academicSetup:ib_grade_boundaries");
        }
      }
      break;
    case ASSESSMENT_CATEGORY_SETUP:
      {
        entityLabel = t("academicSetup:assessment_categories");
      }
      break;
    case LOCAL_GRADE_SETUP: {
      entityLabel = t("common:local_grades");
    }
  }

  switch (emptyStateType) {
    case "TERMS":
      {
        if (getIsNewAssessmentSettingsEnabled()) {
          illustration = AcademicHonestyIllustration;
          title = t("academicSetup:no_grading_periods_defined_yet");
          subtitle = t(
            "academicSetup:no_grading_periods_empty_state_subtext_with_label",
            { label: entityLabel }
          );

          const routeForNewAdminNavigation = isAccountPortal
            ? `/platform/accountSettings/grading-periods/${curriculumProgramId}`
            : `/platform/school/${curriculumProgramId}/grading-periods`;

          route = !(isAccountPortal || isSchoolPortal)
            ? `/platform/administrator/${curriculumProgramId}/${getCurriculumBasedRoute(
                { curriculumType }
              )}/grading-periods`
            : routeForNewAdminNavigation;

          buttonText = t("academicSetup:go_to_grading_period_setup");
          buttonProps = {
            size: "large",
            variant: "outlined",
          };
        } else {
          illustration = NoDataIllustration;
          title = t("academicSetup:no_terms_added_yet");
          subtitle = t("academicSetup:no_terms_subtext");

          route = "/platform/administrator/schoolSetup/academic-year-terms";

          buttonText = t("academicSetup:create_academic_terms");
          buttonProps = {
            size: "large",
            variant: "progressive",
          };
        }
      }
      break;
    case "SUBJECTS": {
      illustration = NoServiceAsActionInsightsIllustration;
      title = t("integration:no_subjects_added_yet");
      subtitle = t("academicSetup:no_subjects_empty_state_subtext_with_label", {
        label: entityLabel,
      });

      const subjectsRouteFragment = isCurriculumMYP
        ? "subjectGroupsAndSubjects"
        : "subjects";

      let newAdminNavigationRoute;

      if (!getIsAccountConfigurationEnabled()) {
        const subjectFragmentForNonMYP = isCurriculumDP
          ? "dp-subjects/subjects"
          : "subjects";

        const subjectFragment = isCurriculumMYP
          ? "subjectGroupsAndSubjects"
          : subjectFragmentForNonMYP;

        newAdminNavigationRoute = isAccountPortal
          ? `/platform/accountSettings/${curriculumProgramId}/${subjectFragment}`
          : `/platform/school/${curriculumProgramId}/${subjectFragment}`;
      } else {
        const subjectFragmentForNonMYP = isCurriculumDP
          ? "dp-subjects/subjects-v2"
          : "subjects-v2";

        newAdminNavigationRoute = isAccountPortal
          ? `/platform/accountSettings/${curriculumProgramId}/${subjectFragmentForNonMYP}`
          : `/platform/school/${curriculumProgramId}/${subjectFragmentForNonMYP}`;
      }

      route = !(isAccountPortal || isSchoolPortal)
        ? `/platform/administrator/${curriculumProgramId}/${getCurriculumBasedRoute(
            { curriculumType }
          )}/${subjectsRouteFragment}`
        : newAdminNavigationRoute;

      buttonText = t("academicSetup:go_to_subjects");
      buttonProps = {
        size: "large",
        variant: "outlined",
      };
    }
  }

  const handleButtonClick = () => {
    goToRelativeRoute({
      route,
      type: "push",
    });
  };

  const primaryButton = (
    <Button onClick={handleButtonClick} {...buttonProps}>
      {buttonText}
    </Button>
  );

  return {
    illustration,
    title,
    subtitle,
    primaryButton,
  };
};

export const getCategoryGradeBoundaryToggleLabel = ({
  t,
  isCurriculumUBD,
  finalGradeCalculationMethod,
}) => {
  return isCurriculumUBD
    ? t("academicSetup:use_grade_boundary_for_each_category")
    : _.includes([MANUAL, MYP_CRITERIA], finalGradeCalculationMethod)
    ? t("academicSetup:assign_an_ib_grade_to_each_category")
    : t("academicSetup:define_grade_boundaries_for_each_category");
};

export const getIsUsingRoundedCategoryScoresInFinalScoreCalculation = () =>
  ACLStore.can("FeatureFlag:UseRoundedCategoryScoresInFinalScoreCalc");

export const getShowTermTotalDisplayTypeSetting = () =>
  ACLStore.can("FeatureFlag:ShowTermTotalDisplayTypeSetting");

export const getShouldReadCriteriaFromTask = () =>
  ACLStore.can("FeatureFlag:ReadMYPCriteriaFromTask");

export const getIsGradingSetupDecimalConfigEnabled = () =>
  ACLStore.can("FeatureFlag:IsEnableGradingSetupDecimalConfig");

export const DECIMAL_VALUES = [0, 1, 2];

export const getDecimalSettingText = ({ t, decimalType, decimalPoints }) => {
  const decimalTypeText =
    decimalType === "ROUND" ? t("common:rounded") : t("common:truncated");

  let decimalPointsText = "";

  switch (_.toString(decimalPoints)) {
    case "0":
      decimalPointsText = t("academicSetup:in_whole_numbers");
      break;
    case "1":
      decimalPointsText = t("academicSetup:upto_1_decimal_value");
      break;
    default:
      decimalPointsText = t("academicSetup:upto_2_decimal_values");
  }

  return `${decimalPointsText}, ${_.toLower(decimalTypeText)}`;
};

export const getDecimalValuesText = ({ t, value }) => {
  switch (_.toString(value)) {
    case "0":
      return t("academicSetup:no_decimal_values");
    case "1":
      return t("academicSetup:1_decimal_value");
    default:
      return t("academicSetup:2_decimal_values");
  }
};

export const getDecimalTypeOptions = ({ t, decimalValue }) => {
  return [
    {
      value: "ROUND",
      label: t("academicSetup:score_type_with_subtext", {
        scoreType: t("academicSetup:round_scores"),
        subtext: t("academicSetup:round_decimal_values_example", {
          label: _.round(94.885, decimalValue),
        }),
      }),
    },
    {
      value: "TRUNCATE",
      label: t("academicSetup:score_type_with_subtext", {
        scoreType: t("academicSetup:truncate_scores"),
        subtext: t("academicSetup:truncate_decimal_values_example", {
          label:
            Math.trunc(94.885 * Math.pow(10, decimalValue)) /
            Math.pow(10, decimalValue),
        }),
      }),
    },
  ];
};

export const getPreviousAcademicYearBlockedTooltip = ({
  t,
  placement = "bottom",
}) => (
  <Tooltip
    tooltip={t(
      "schoolSetup:navigate_to_the_current_academic_year_to_edit_with_label",
      {
        label: t("schoolSetup:change_this_setting"),
      }
    )}
    placement={placement}
  />
);

export const getIsLocalGradeEnabled = ({
  curriculumProgramType,
  isOverallGradeCalculated,
  isLocalGradeEnabled,
}) =>
  curriculumProgramType == CURRICULUM_TYPE_UBD
    ? isOverallGradeCalculated
    : isLocalGradeEnabled;

const getNomenclatureLabels = ({ t, curriculumProgramType, id }) => {
  switch (id) {
    case FINAL_GRADE: {
      switch (curriculumProgramType) {
        case CURRICULUM_TYPE_MYP: {
          return {
            fixedLabel: t("academicSetup:overall_ibmyp_grade_for_subjects"),
            labelForSubtitle: t("academicSetup:overall_ibmyp_grade"),
          };
        }
        case CURRICULUM_TYPE_DP: {
          return {
            fixedLabel: t("academicSetup:overall_ibdp_grade_for_subjects"),
            labelForSubtitle: t("academicSetup:overall_ibdp_grade"),
          };
        }
        default: {
          return {
            fixedLabel: t("academicSetup:overall_grade_for_subjects"),
            labelForSubtitle: t("common:overall_grade"),
          };
        }
      }
    }
    case LOCAL_GRADE: {
      switch (curriculumProgramType) {
        case CURRICULUM_TYPE_UBD: {
          return {
            fixedLabel: t("academicSetup:overall_grade_for_subjects"),
            labelForSubtitle: t("common:overall_grade"),
          };
        }
        default: {
          return {
            fixedLabel: t("academicSetup:local_grade_for_subjects"),
            labelForSubtitle: t("common:local_grade_text"),
          };
        }
      }
    }
  }
};

export const getNomenclatureList = ({ t, curriculumProgramType }) => {
  return [
    {
      id: FINAL_SCORE,
      fixedLabel: t("academicSetup:overall_score_for_subjects"),
      labelForSubtitle: t("academicSetup:overall_score"),
    },
    {
      id: FINAL_GRADE,
      ...getNomenclatureLabels({
        t,
        curriculumProgramType,
        id: FINAL_GRADE,
      }),
    },
    {
      id: LOCAL_GRADE,
      ...getNomenclatureLabels({
        t,
        curriculumProgramType,
        id: LOCAL_GRADE,
      }),
    },
    {
      id: CATEGORY_SCORE,
      fixedLabel: t("academicSetup:score_for_categories"),
      labelForSubtitle: t("academicSetup:category_scores"),
    },
    {
      id: CATEGORY_GRADE,
      fixedLabel: t("academicSetup:grade_for_categories"),
      labelForSubtitle: t("academicSetup:category_grades"),
    },
  ];
};

export const nomenclaturesForScores = [FINAL_SCORE, CATEGORY_SCORE];

// In UbD curriculum local grade is used as overall grade so, we also need to handle category grade based on overall grade setting
export const getNomenclaturesForLocalGrade = ({ curriculumProgramType }) => {
  switch (curriculumProgramType) {
    case CURRICULUM_TYPE_UBD: {
      return [LOCAL_GRADE, CATEGORY_GRADE];
    }
    default: {
      return [LOCAL_GRADE];
    }
  }
};

export const getExcludedNomenclatures = ({ curriculumProgramType }) => {
  switch (curriculumProgramType) {
    case CURRICULUM_TYPE_PYP: {
      return [FINAL_GRADE, CATEGORY_GRADE];
    }
    case CURRICULUM_TYPE_UBD: {
      return [FINAL_GRADE];
    }
    default: {
      return [];
    }
  }
};

// This function will check if the permission is applicable for criteria
// If yes then it will check the permission value otherwise, it will return true
const checkIsNomenclatureEnabledByPermission = ({
  id,
  nomenclaturesForCriteria,
  perm,
}) => !_.includes(nomenclaturesForCriteria, id) || perm;

const getIsValidNomenclature = ({
  curriculumProgramType,
  isOverallScoreEnabled,
  isLocalGradeEnabled,
  id,
}) => {
  const excludedNomenclaturesForCurriculum = getExcludedNomenclatures({
    curriculumProgramType,
  });
  const isExcludedNomenclature = !_.includes(
    excludedNomenclaturesForCurriculum,
    id
  );

  const nomenclaturesForLocalGrade = getNomenclaturesForLocalGrade({
    curriculumProgramType,
  });

  const isNomenclatureForScoresEnabled = checkIsNomenclatureEnabledByPermission(
    {
      id,
      nomenclaturesForCriteria: nomenclaturesForScores,
      perm: isOverallScoreEnabled,
    }
  );
  const isNomenclatureForLocalGradeEnabled = checkIsNomenclatureEnabledByPermission(
    {
      id,
      nomenclaturesForCriteria: nomenclaturesForLocalGrade,
      perm: isLocalGradeEnabled,
    }
  );

  return (
    isExcludedNomenclature &&
    isNomenclatureForScoresEnabled &&
    isNomenclatureForLocalGradeEnabled
  );
};

export const getNomenclaturesForCurriculum = ({
  curriculumProgramType,
  isOverallScoreEnabled,
  isLocalGradeEnabled,
  t,
}) => {
  const nomenclatureList = getNomenclatureList({ t, curriculumProgramType });
  const filteredNomenclatureList = _.filter(nomenclatureList, ({ id }) => {
    return getIsValidNomenclature({
      curriculumProgramType,
      isOverallScoreEnabled,
      isLocalGradeEnabled,
      id,
    });
  });
  return filteredNomenclatureList;
};

export const getMemoizedNomenclaturesForCurriculum = _.memoize(
  params => getNomenclaturesForCurriculum(params),
  params => JSON.stringify(params)
);

export const getNomenclatureListForSubtext = ({
  t,
  curriculumProgramType,
  isOverallScoreEnabled,
  isLocalGradeEnabled,
  nomenclatureList, // Passing nomenclature list if it is already computed
}) => {
  const updatedNomenclatureList = _.isNil(nomenclatureList)
    ? getMemoizedNomenclaturesForCurriculum({
        t,
        curriculumProgramType,
        isOverallScoreEnabled,
        isLocalGradeEnabled,
      })
    : nomenclatureList;

  return _.map(
    updatedNomenclatureList,
    ({ labelForSubtitle }) => labelForSubtitle
  );
};

export const getNomenclatureData = ({
  nomenclatureList,
  nomenclatureLabels,
}) => {
  return _.reduce(
    nomenclatureList,
    (acc, nomenclature) => {
      const { id, labelForSubtitle } = nomenclature;
      let subTypeToFind;
      let criteriaTypeToFind;
      switch (id) {
        case CATEGORY_SCORE: {
          subTypeToFind = SCORE;
          criteriaTypeToFind = CATEGORY_PLACEHOLDER;
          break;
        }
        case CATEGORY_GRADE: {
          subTypeToFind = GRADE;
          criteriaTypeToFind = CATEGORY_PLACEHOLDER;
          break;
        }
        default: {
          subTypeToFind = null;
          criteriaTypeToFind = id;
        }
      }

      const nomenclatureValue = _.find(
        nomenclatureLabels,
        ({ criteriaType, subType }) => {
          const comparedSubType = !_.isNil(subTypeToFind)
            ? subTypeToFind == subType
            : true;
          return criteriaTypeToFind == criteriaType && comparedSubType;
        }
      );

      acc[id] = {
        id: nomenclatureValue?.id,
        label: nomenclatureValue?.label,
        labelForSubtitle,
      };
      return acc;
    },
    {}
  );
};

export const getUpdateNomenclatures = ({
  oldNomenclatureData,
  updatedNomenclatureData,
  nomenclatureList,
}) => {
  return _.reduce(
    nomenclatureList,
    (acc, { id: criteriaId }) => {
      const oldLabel = _.get(oldNomenclatureData, `${criteriaId}.label`, "");
      const updatedData = _.get(updatedNomenclatureData, criteriaId, {});

      if (!_.isEqual(oldLabel, updatedData.label)) {
        acc.push(_.pick(updatedData, ["id", "label", "labelForSubtitle"]));
      }
      return acc;
    },
    []
  );
};

export const getNomenclaturesFilters = ({ currentCurriculumProgramId }) => ({
  curriculumProgramIds: [currentCurriculumProgramId],
  criteriaTypes: [FINAL_SCORE, LOCAL_GRADE, FINAL_GRADE, CATEGORY_PLACEHOLDER],
  categoryGradeSubTypes: [SCORE, GRADE],
  finalGradeSubTypes: [SUBJECT],
  localGradeSubTypes: [FINAL_GRADE, FINAL_SCORE, DEFAULT],
  archivalState: ACTIVE,
});

export const GRADE_WISE_GRADING_SETUP_CONFIG = [
  FINAL_GRADE_CALCULATION_SETTING,
];

export const getIsGradeLevelConfigForGradeCalculationEnabled = () =>
  ACLStore.can("FeatureFlag:IsEnableGradeLevelConfigForGradeCalculation");

export const getIsGradebookForCASEnabled = () =>
  ACLStore.can("Common:GradebookForCAS");

export const getSubjectsWithNoGradebook = () => {
  const subjectsWithNoGradebook = ["EE"];

  if (!getIsGradebookForCASEnabled()) {
    subjectsWithNoGradebook.push("CAS");
  }

  return subjectsWithNoGradebook;
};
